import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";

function Shell({ title, children, foot }: { title: string; children: React.ReactNode; foot?: React.ReactNode }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0f1f1d] text-white p-12">
        <Link to="/" className="self-start bg-white rounded-xl px-4 py-3" aria-label="Instant Quote Wizard home"><BrandLogo className="h-16" testid="auth-brand-logo" /></Link>
        <p className="font-heading text-3xl font-bold leading-tight max-w-md">Satellite-measured estimates, priced by your rules.</p>
        <p className="text-xs text-white/50">Preliminary estimates only · Not a binding agreement</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <Link to="/" className="lg:hidden inline-block mb-8" aria-label="Instant Quote Wizard home"><BrandLogo className="h-14" testid="auth-brand-logo-mobile" /></Link>
          <h1 className="text-2xl font-bold mb-6">{title}</h1>
          {children}
          {foot && <div className="mt-6 text-sm text-muted-foreground">{foot}</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ id, label, ...p }: any) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} className="h-11" {...p} />
    </div>
  );
}

const ErrorText = ({ msg }: { msg: string }) => (msg ? <p className="text-sm text-destructive" role="alert" data-testid="auth-error">{msg}</p> : null);

export function Login() {
  const { setUser, user } = useAuth();
  const nav = useNavigate();
  const [q] = useSearchParams();
  const [f, setF] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav(q.get("next") || "/app", { replace: true }); }, [user, nav, q]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/login", f); setUser(data); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Sign in" foot={<>New here? <Link className="text-primary underline" to="/register" data-testid="to-register">Create an account</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <Field id="email" label="Email" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="login-email-input" />
        <Field id="password" label="Password" type="password" autoComplete="current-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="login-password-input" />
        <ErrorText msg={error} />
        <Button className="w-full h-11" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</Button>
        <Link to="/forgot-password" className="block text-sm text-muted-foreground underline" data-testid="forgot-link">Forgot password?</Link>
      </form>
    </Shell>
  );
}

export function Register() {
  const { setUser, user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ business_name: "", email: "", password: "", accept_terms: false });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) nav("/app", { replace: true }); }, [user, nav]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/register", f); setUser(data); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell title="Create your company account" foot={<>Already have an account? <Link className="text-primary underline" to="/login">Sign in</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <Field id="biz" label="Business name" required value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="register-business-input" />
        <Field id="email" label="Work email" type="email" autoComplete="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="register-email-input" />
        <Field id="pw" label="Password (10+ characters)" type="password" autoComplete="new-password" minLength={10} required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="register-password-input" />
        <label className="flex items-start gap-3 text-sm">
          <Checkbox checked={f.accept_terms} onCheckedChange={(v) => setF({ ...f, accept_terms: !!v })} className="mt-0.5" data-testid="register-terms-checkbox" />
          <span>I agree to the <Link to="/terms" className="underline" target="_blank">Terms</Link> and <Link to="/privacy" className="underline" target="_blank">Privacy Policy</Link>.</span>
        </label>
        <ErrorText msg={error} />
        <Button className="w-full h-11" disabled={busy || !f.accept_terms} data-testid="register-submit-button">{busy ? "Creating…" : "Create account"}</Button>
      </form>
    </Shell>
  );
}

export function Forgot() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError("");
    try { const { data } = await api.post("/auth/forgot-password", { email }); setDone(data.message); } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Shell title="Reset your password" foot={<Link to="/login" className="underline">Back to sign in</Link>}>
      {done ? <p data-testid="forgot-done">{done}</p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="email" label="Email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" />
          <ErrorText msg={error} />
          <Button className="w-full h-11" data-testid="forgot-submit-button">Send reset link</Button>
        </form>
      )}
    </Shell>
  );
}

export function Reset() {
  const [q] = useSearchParams();
  const [pw, setPw] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError("");
    try { await api.post("/auth/reset-password", { token: q.get("token") || "", password: pw }); setDone(true); } catch (err) { setError(errMsg(err)); }
  };
  return (
    <Shell title="Choose a new password">
      {done ? <p data-testid="reset-done">Password updated. <Link to="/login" className="underline text-primary">Sign in</Link></p> : (
        <form onSubmit={submit} className="space-y-4">
          <Field id="pw" label="New password (10+ characters)" type="password" minLength={10} required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" />
          <ErrorText msg={error} />
          <Button className="w-full h-11" data-testid="reset-submit-button">Update password</Button>
        </form>
      )}
    </Shell>
  );
}

export function ConfirmReplyTo() {
  const [q] = useSearchParams();
  const [state, setState] = useState<"working" | "ok" | string>("working");
  useEffect(() => {
    api.post("/public/reply-to/confirm", { token: q.get("token") || "" }).then(() => setState("ok")).catch((e) => setState(errMsg(e)));
  }, [q]);
  return (
    <Shell title="Reply-To confirmation">
      <p data-testid="reply-to-confirm-status">{state === "working" ? "Confirming…" : state === "ok" ? "Address confirmed. Homeowner replies will now go to this email." : state}</p>
      <Link to="/app/business" className="mt-4 inline-block underline text-primary">Go to Business Profile</Link>
    </Shell>
  );
}

export function Verify() {
  const [q] = useSearchParams();
  const [state, setState] = useState<"working" | "ok" | string>("working");
  useEffect(() => {
    api.post("/auth/verify-email", { token: q.get("token") || "" }).then(() => setState("ok")).catch((e) => setState(errMsg(e)));
  }, [q]);
  return (
    <Shell title="Email confirmation">
      <p data-testid="verify-status">{state === "working" ? "Confirming…" : state === "ok" ? "Your email is confirmed." : state}</p>
      <Link to="/app" className="mt-4 inline-block underline text-primary">Go to dashboard</Link>
    </Shell>
  );
}
